import type { DocumentData, QuerySnapshot, Unsubscribe, UpdateData } from 'firebase/firestore'
import { addDoc, collection, deleteDoc, deleteField, doc, onSnapshot, serverTimestamp, updateDoc } from 'firebase/firestore'
import { defineStore } from 'pinia'
import { useFirestore } from 'vuefire'
import { SavedPrompt, sortSavedPrompts } from '@/models/SavedPrompt'
import { logger } from '@/utils/logger'
import i18n from '@/plugins/i18n'
import { useAppStore } from './app'
import { useUsersStore } from './users'

/**
 * Saved chat prompts store (MYAC-1106) — `projects/{pid}/saved_prompts`, a
 * tier-1 register mirroring `stores/partners.ts`. Phase 1 only reads (the
 * chat picker); the CRUD actions exist for the phase-2 editor and the shape
 * they write is exactly what the rules allow-list accepts.
 */

export interface SavedPromptPayload {
  name: string
  description: string | null
  body: string
  order: number
  enabled: boolean
}

export const SAVED_PROMPT_SCHEMA_VERSION = 1

export const useSavedPromptsStore = defineStore('savedPrompts', {
  state: () => ({
    isLoading: false,
    /** True after at least one successful snapshot for the current pid. */
    hasLoaded: false,
    /** True when the subscription errored — the UI must not show "no prompts". */
    loadFailed: false,
    _current_pid: '' as string,
    _snapshot: null as QuerySnapshot<DocumentData, DocumentData> | null,
    _unsub: null as Unsubscribe | null,
  }),

  getters: {
    /** Every prompt of the project (enabled or not), in display order. */
    all: (state): SavedPrompt[] =>
      sortSavedPrompts(
        (state._snapshot?.docs ?? [])
          .map((d) => SavedPrompt.fromFirebase(d))
          .filter((p): p is SavedPrompt => p !== null),
      ),
    /** What the chat picker shows: enabled prompts only. */
    prompts(): SavedPrompt[] {
      return this.all.filter((p) => p.enabled)
    },
    count(): number {
      return this.prompts.length
    },
  },

  actions: {
    unsubscribe() {
      if (this._unsub) {
        this._unsub()
        this._unsub = null
      }
      this._current_pid = ''
      this._snapshot = null
      this.isLoading = false
      this.hasLoaded = false
      this.loadFailed = false
    },

    /** Subscribe to one project's prompts; same pid → no-op, new pid → re-subscribe. */
    subscribe(pid: string) {
      if (!pid) {
        this.unsubscribe()
        return
      }
      if (this._unsub && this._current_pid === pid) return
      this.unsubscribe()
      this._current_pid = pid
      this.isLoading = true

      const firestore = useFirestore()
      const ref = collection(firestore, SavedPrompt.collectionPath(pid))
      this._unsub = onSnapshot(
        ref,
        (snap) => {
          this._snapshot = snap
          this.isLoading = false
          this.hasLoaded = true
          this.loadFailed = false
        },
        (error) => {
          logger.error('saved_prompts subscribe error', error)
          // A listener that errors is dead: drop it so a later subscribe(pid)
          // can re-establish it, and never report "loaded" off a failed read.
          if (this._unsub) {
            this._unsub()
            this._unsub = null
          }
          this._current_pid = ''
          this._snapshot = null
          this.isLoading = false
          this.hasLoaded = false
          this.loadFailed = true
        },
      )
    },

    async create(pid: string, payload: SavedPromptPayload): Promise<string | null> {
      const name = payload.name?.trim()
      if (!pid || !name) {
        logger.error('savedPrompts.create: missing pid or name', { pid })
        useAppStore().toast(
          i18n.global.t('chat.savedPrompts.errors.title'),
          i18n.global.t('chat.savedPrompts.errors.create_failed'),
          'error',
        )
        return null
      }
      try {
        const uid = useUsersStore().uid
        const firestore = useFirestore()
        const ref = collection(firestore, SavedPrompt.collectionPath(pid))
        const description = payload.description?.trim()
        const data: Record<string, unknown> = {
          name,
          body: payload.body,
          order: payload.order,
          enabled: payload.enabled,
          created_at: serverTimestamp(),
          created_by: uid,
          updated_at: serverTimestamp(),
          updated_by: uid,
          schema_version: SAVED_PROMPT_SCHEMA_VERSION,
        }
        if (description) data.description = description
        const created = await addDoc(ref, data)
        return created.id
      } catch (error) {
        logger.error('savedPrompts.create failed', error)
        useAppStore().toast(
          i18n.global.t('chat.savedPrompts.errors.title'),
          i18n.global.t('chat.savedPrompts.errors.create_failed'),
          'error',
        )
        return null
      }
    },

    /**
     * @returns true when the write landed, false when it did not. This action
     * handles its own failure (it logs and toasts, and never throws), so a
     * caller holding unsaved user input has no other way to tell — and closing
     * an editor on a failed write discards what the member typed (MYAC-1140).
     */
    async update(pid: string, id: string, patch: Partial<SavedPromptPayload>): Promise<boolean> {
      if (!pid || !id) return false
      try {
        const uid = useUsersStore().uid
        const firestore = useFirestore()
        const ref = doc(firestore, SavedPrompt.docPath(pid, id))
        const data: UpdateData<DocumentData> = {
          ...patch,
          updated_at: serverTimestamp(),
          updated_by: uid,
        }
        // MYAC-1140 — an empty description REMOVES the field; it must never be
        // written as null. The rules type-check `description` against the MERGED
        // document (`request.resource.data` on an update is the doc as it would
        // be after the write), and their guard is
        //   !('description' in data) || data.description is string
        // A null keeps the key present and fails `is string`, so the whole write
        // is rejected — which killed not just "clear the description" but EVERY
        // edit of a prompt that never had one, since the editor sends the field
        // on every save. `create` already omits it when empty; this makes the
        // update path agree, and leaves the stored shape absent-not-null.
        if ('description' in patch && !patch.description) data.description = deleteField()
        await updateDoc(ref, data)
        return true
      } catch (error) {
        logger.error('savedPrompts.update failed', error)
        useAppStore().toast(
          i18n.global.t('chat.savedPrompts.errors.title'),
          i18n.global.t('chat.savedPrompts.errors.update_failed'),
          'error',
        )
        return false
      }
    },

    async remove(pid: string, id: string): Promise<void> {
      if (!pid || !id) return
      try {
        const firestore = useFirestore()
        await deleteDoc(doc(firestore, SavedPrompt.docPath(pid, id)))
      } catch (error) {
        logger.error('savedPrompts.remove failed', error)
        useAppStore().toast(
          i18n.global.t('chat.savedPrompts.errors.title'),
          i18n.global.t('chat.savedPrompts.errors.delete_failed'),
          'error',
        )
      }
    },
  },
})
